import { isFuture, isValid, parseISO, startOfDay } from 'date-fns';
import { NtaPatientStatus, NtaPatientStatusOptions } from './patient.typings';

export interface NormalizePatientStatusInput {
	existingStatus?: NtaPatientStatus | string | null;
	admitDate?: unknown;
	dischargeDate?: unknown;
	pccPatientStatus?: string | null;
	pccIsOnLeave?: boolean;
}

function toCanonicalPatientStatus(status?: string | null): NtaPatientStatus | null {
	if (!status) return null;
	if (NtaPatientStatusOptions.includes(status as NtaPatientStatus)) return status as NtaPatientStatus;
	return null;
}

function toDate(value: unknown): Date | null {
	if (!value) return null;
	if (value instanceof Date) return value;
	if (typeof value === 'string') {
		const parsed = parseISO(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}
	if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function') {
		try {
			const parsed = (value as { toDate: () => Date }).toDate();
			return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
		} catch {
			return null;
		}
	}
	return null;
}

function isPastOrToday(dateLike: unknown): boolean {
	const date = toDate(dateLike);
	if (!date) return false;
	return !isFuture(startOfDay(date));
}

function isFutureDate(dateLike: unknown): boolean {
	const date = toDate(dateLike);
	if (!date || !isValid(date)) return false;
	return isFuture(startOfDay(date));
}

export function normalizeNtaPatientStatus(input: NormalizePatientStatusInput): NtaPatientStatus {
	const pccStatus = toCanonicalPatientStatus(input.pccPatientStatus);
	const existingStatus = toCanonicalPatientStatus(input.existingStatus ?? null);
	const isDischarged = isPastOrToday(input.dischargeDate);
	const isFutureAdmit = isFutureDate(input.admitDate);

	if (pccStatus) {
		if (input.pccIsOnLeave) {
			return 'OnLeave';
		}
		return pccStatus;
	}

	if (isDischarged) {
		return 'Discharged';
	}

	if (existingStatus === 'OnLeave') {
		return 'OnLeave';
	}

	if (isFutureAdmit) {
		return 'New';
	}

	if (existingStatus) {
		return existingStatus;
	}

	return 'Current';
}
